// @dx form
//
// EntitySelect — unified Refine-driven Select keyed by `kind`.
//
// One component, many domains. Per-kind config (resource, select, sorters,
// label/search renderers, base filters, optional grouped options, optional
// custom optionRender, optional "allowed values" pre-fetch) lives in the
// REGISTRY below. Adding a new entity kind = one registry entry, no new file.
//
// Discriminated-union props mean each kind's extension props are typed:
//   <EntitySelect kind="company"  category="recycler" />
//   <EntitySelect kind="employee" role="sales" />
//   <EntitySelect kind="product"  context="transactions" />
//   <EntitySelect kind="wasteCode" filterHazardous onlyFromProducts />
//
// Replaces the previous 7 thin wrappers (CompanySelect, ProductSelect,
// EmployeeSelect, PayeeSelect, TransactionSelect, WasteCodeSelect,
// RecyclingProcessSelect). See `decisions.md → ## entity-select`.

import React, { useState, useMemo, useRef, useEffect } from "react";
import { Select, type SelectProps, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { useList, type CrudFilters } from "@refinedev/core";
import { FONT_SIZE, LAYOUT } from "@ds/tokens/palette";
import { Inline } from "@ds/layout/Inline";
import { TextValue } from "@ds/display/TextValue";

import type { Company, CompanyUsageContext } from "@shared/types/company";
import type { Product, ProductUsageContext, WasteCode } from "@shared/types/product";
import type { Employee } from "@shared/types/employee";
import type { EmployeeRole } from "@shared/types";
import type { CommissionPayee } from "@shared/types/commission";
import type { Transaction } from "@shared/types/transaction";
import type { RecyclingProcess } from "@shared/types/company_document";
import { CategoriesTags } from "../domain/EnumTag.Composites";

// ─── Kind union + per-kind extension props ──────────────────────────────────

export type EntityKind =
  | "company"
  | "product"
  | "employee"
  | "payee"
  | "wasteCode"
  | "recyclingProcess"
  | "transaction";

interface CompanyExt {
  category?: string | string[];
  categories?: string[];
  context?: CompanyUsageContext;
  requireNip?: boolean;
  excludeIds?: number[];
}
interface ProductExt {
  context?: ProductUsageContext;
}
interface EmployeeExt {
  role?: EmployeeRole;
}
interface WasteCodeExt {
  filterHazardous?: boolean;
  showHazardMark?: boolean;
  onlyFromProducts?: boolean;
  onlyEdprEligible?: boolean;
}

// Empty interfaces for kinds without extension props — distinct from
// `NoExt` whose index signature collapses any extra prop to
// `never` when intersected with BaseSelectProps.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface NoExt {}

// Per-kind ext-props shape (used by registry typing + props discriminated union)
type KindExt = {
  company:           CompanyExt;
  product:           ProductExt;
  employee:          EmployeeExt;
  payee:             NoExt;
  wasteCode:         WasteCodeExt;
  recyclingProcess:  NoExt;
  transaction:       NoExt;
};

// ─── Registry types ─────────────────────────────────────────────────────────

interface KindConfig<R, Ext> {
  /** Refine resource name. May be a function of extension props (e.g. switch
   *  between `companies` and `company_usage_contexts`). */
  resource:    string | ((ext: Ext) => string);
  /** PostgREST select. Static or props-derived. */
  select?:     string | ((ext: Ext) => string);
  /** Refine sorters. */
  sorters?:    Array<{ field: string; order: "asc" | "desc" }>;
  /** Per-kind cache duration (ms) for the list query. Overrides the component
   *  default; the `staleTime` prop still wins. Use for slow-changing reference
   *  data (employees, recycling processes) that needn't refetch each mount. */
  staleTime?:  number;
  /** Static + dynamic filters merged at render. */
  buildFilters?: (ext: Ext) => CrudFilters;
  /** Client-side filter (e.g. requireNip, excludeIds, role). */
  buildFilterFn?: (ext: Ext) => ((r: R) => boolean) | undefined;
  /** Display label. */
  labelFn:     (r: R, ext: Ext) => React.ReactNode;
  /** Searchable text. Default: stringified labelFn output. */
  searchFn?:   (r: R) => string;
  /** Attach extra fields to each option object. */
  optionData?: (r: R) => Record<string, unknown>;
  /** Override option value (default: r.id). */
  valueFn?:    (r: R) => number | string;
  /** Build grouped options instead of flat. Receives filtered records. */
  groupBy?:    (records: R[], t: (k: string) => string) => Array<{
    label: React.ReactNode;
    options: Array<{ label: React.ReactNode; value: number | string }>;
  }>;
  /** Custom AntD optionRender (used by wasteCode for hazardous tag). */
  optionRender?: (option: { label: React.ReactNode; data?: Record<string, unknown> }, ext: Ext) => React.ReactNode;
  /** Pre-fetch a sibling resource and intersect by a field. */
  allowedValuesPrefetch?: (ext: Ext) => null | {
    resource:   string;
    select:     string;
    enabled:    boolean;
    extractKey: (row: Record<string, unknown>) => unknown;
    matchKey:   (record: R) => unknown;
  };
  /** Override AntD `filterOption` (e.g. wasteCode uses `optionFilterProp`-like). */
  selectExtras?: (ext: Ext) => Partial<SelectProps>;
  /** i18n key for the placeholder. */
  placeholderKey: string;
  /** Optional fallback placeholder text. */
  placeholderFallback?: string;
}

// ─── Per-kind helpers (label / search / filter-fn closures) ─────────────────

const companyContextColumn: Record<CompanyUsageContext, string> = {
  load_commercial_company:   "as_load_commercial_company",
  unload_commercial_company: "as_unload_commercial_company",
  load_place:                "as_load_place",
  unload_place:              "as_unload_place",
  exporter:                  "as_exporter",
  importer:                  "as_importer",
  invoice_seller:            "as_invoice_seller",
  invoice_buyer:             "as_invoice_buyer",
  price_list_company:        "as_price_list_company",
  price_list_load_place:     "as_price_list_load_place",
  price_list_unload_place:   "as_price_list_unload_place",
};

const productContextColumn: Record<ProductUsageContext, string> = {
  transactions: "in_transactions",
  price_lists:  "in_price_lists",
};

const sortByNumericCode = (a: RecyclingProcess, b: RecyclingProcess) =>
  parseInt(a.code.slice(1), 10) - parseInt(b.code.slice(1), 10);

// ─── Registry ───────────────────────────────────────────────────────────────
//
// Per-kind config objects are typed against their *specific* record + ext
// types. The map itself is widened (each entry is "some KindConfig") so the
// registry literal accepts each of them without variance complaints. Callers
// re-narrow at lookup via the `kind` discriminator.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyKindConfig = KindConfig<any, any>;

const REGISTRY: { [K in EntityKind]: AnyKindConfig } = {
  company: {
    resource: (ext) => (ext.context ? "company_usage_contexts" : "companies"),
    select:   "id, short_name, city, nip, categories",
    sorters:  [{ field: "short_name", order: "asc" }],
    buildFilters: (ext) => {
      const result: CrudFilters = [
        { field: "is_deleted", operator: "eq", value: false },
      ];
      const categoryFilter =
        ext.categories ??
        (ext.category ? (Array.isArray(ext.category) ? ext.category : [ext.category]) : undefined);
      if (categoryFilter && categoryFilter.length > 0) {
        result.push({ field: "categories", operator: "ina", value: categoryFilter });
      }
      if (ext.context) {
        result.push({ field: companyContextColumn[ext.context], operator: "eq", value: true });
      }
      return result;
    },
    buildFilterFn: (ext) => {
      const excludeSet = ext.excludeIds && ext.excludeIds.length > 0 ? new Set(ext.excludeIds) : undefined;
      if (!ext.requireNip && !excludeSet) return undefined;
      return (c: Company) => {
        if (excludeSet?.has(c.id)) return false;
        if (ext.requireNip && (c.nip == null || c.nip === "")) return false;
        return true;
      };
    },
    labelFn: (c: Company) => (
      <Inline as="span" gap="xs" align="center">
        {c.short_name}
        {c.categories?.length > 0 && (
          <CategoriesTags values={c.categories} mode="compact" />
        )}
      </Inline>
    ),
    searchFn:   (c: Company) => [c.short_name, c.city, c.nip].filter(Boolean).join(" "),
    optionData: (c: Company) => ({ company: c }),
    placeholderKey: "companies.placeholders.select",
  } as KindConfig<Company, CompanyExt>,

  product: {
    resource: (ext) => (ext.context ? "product_usage_contexts" : "products"),
    select:   "id, name, waste_code",
    sorters:  [{ field: "name", order: "asc" }],
    buildFilters: (ext) => {
      const result: CrudFilters = [
        { field: "is_deleted", operator: "eq", value: false },
      ];
      if (ext.context) {
        result.push({ field: productContextColumn[ext.context], operator: "eq", value: true });
      }
      return result;
    },
    labelFn: (p: Product) => `${p.name}${p.waste_code ? ` (${p.waste_code})` : ""}`,
    searchFn: (p: Product) => [p.name, p.waste_code].filter(Boolean).join(" "),
    placeholderKey: "products.placeholders.select",
  } as KindConfig<Product, ProductExt>,

  employee: {
    resource: "employees",
    select:   "id, full_name, email, roles",
    sorters:  [{ field: "full_name", order: "asc" }],
    // Employees change rarely — cache for a few hours so the select doesn't
    // refetch on every mount.
    staleTime: 4 * 60 * 60 * 1000,
    buildFilters: () => [{ field: "is_deleted", operator: "eq", value: false }],
    buildFilterFn: (ext) => {
      const role = ext.role;
      return role ? (e: Employee) => !!e.roles?.includes(role) : undefined;
    },
    labelFn:  (e: Employee) => e.full_name,
    searchFn: (e: Employee) => [e.full_name, e.email].filter(Boolean).join(" "),
    placeholderKey: "employees.placeholders.select",
    placeholderFallback: "Wybierz pracownika...",
  } as KindConfig<Employee, EmployeeExt>,

  payee: {
    resource: "commission_payees",
    select:   "id, name, employee_id, company_id",
    sorters:  [{ field: "name", order: "asc" }],
    buildFilters: () => [{ field: "is_deleted", operator: "eq", value: false }],
    labelFn:  (p: CommissionPayee) => p.name,
    searchFn: (p: CommissionPayee) => p.name,
    placeholderKey: "commission.actions.select_payee",
    placeholderFallback: "Wybierz odbiorcę...",
  } as KindConfig<CommissionPayee, NoExt>,

  wasteCode: {
    resource: "waste_codes",
    sorters:  [{ field: "code", order: "asc" }],
    buildFilters: (ext) => {
      const result: CrudFilters = [];
      if (typeof ext.filterHazardous === "boolean") {
        result.push({ field: "is_hazardous", operator: "eq", value: ext.filterHazardous });
      }
      if (ext.onlyEdprEligible) {
        result.push({ field: "is_edpr_eligible", operator: "eq", value: true });
      }
      return result;
    },
    valueFn: (w: WasteCode) => w.code,
    labelFn: (w: WasteCode, ext) => {
      const showHazardMark = ext.showHazardMark ?? true;
      const hazardMark = showHazardMark && w.is_hazardous ? " *" : "";
      return `${w.code}${hazardMark} - ${w.description}`;
    },
    searchFn: (w: WasteCode) => `${w.code} ${w.description}`,
    optionData: (w: WasteCode) => ({ isHazardous: w.is_hazardous }),
    optionRender: (option, ext) => {
      const showHazardMark = ext.showHazardMark ?? true;
      const isHaz = !!option.data?.isHazardous;
      return (
        <Inline justify="between" align="start" wrap>
          <TextValue as="span" wrapWords>{option.label}</TextValue>
          {showHazardMark && isHaz && (
            <Tag color="red" style={{ marginLeft: 8, fontSize: FONT_SIZE.label, flexShrink: 0 }}>
              Niebezpieczny
            </Tag>
          )}
        </Inline>
      );
    },
    allowedValuesPrefetch: (ext) =>
      ext.onlyFromProducts
        ? {
            resource:   "products",
            select:     "waste_code",
            enabled:    true,
            extractKey: (row) => (row as { waste_code?: string }).waste_code,
            matchKey:   (w) => (w).code,
          }
        : null,
    selectExtras: () => ({
      optionFilterProp: "label",
      filterOption: (input: string, option) => {
        if (!input) return true;
        const label = option?.label;
        if (typeof label !== "string") return false;
        return label.toLowerCase().includes(input.toLowerCase());
      },
      // AntD v5 current API — `dropdownStyle` is deprecated.
      styles: { popup: { root: { minWidth: 480, maxWidth: 600 } } },
    }),
    placeholderKey: "waste_codes.placeholders.select",
  } as KindConfig<WasteCode, WasteCodeExt>,

  recyclingProcess: {
    resource: "recycling_processes",
    sorters:  undefined, // grouped client-side
    valueFn:  (p: RecyclingProcess) => p.code,
    labelFn:  (p: RecyclingProcess) => {
      const desc = p.description.length > 60 ? p.description.slice(0, 60) + "…" : p.description;
      return `${p.code} — ${desc}`;
    },
    searchFn: (p: RecyclingProcess) => `${p.code} ${p.description}`,
    groupBy: (records, t) => {
      const all = records;
      const recovery = all.filter((p) => p.type === "recovery").sort(sortByNumericCode);
      const disposal = all.filter((p) => p.type === "disposal").sort(sortByNumericCode);
      const toOption = (p: RecyclingProcess) => ({
        label: (
          <span data-testid={`entity-select-option-${p.code}`}>
            {`${p.code} — ${p.description.length > 60 ? p.description.slice(0, 60) + "…" : p.description}`}
          </span>
        ),
        value: p.code,
      });
      return [
        { label: t("company_documents.recycling_processes.recovery_group"), options: recovery.map(toOption) },
        { label: t("company_documents.recycling_processes.disposal_group"), options: disposal.map(toOption) },
      ];
    },
    selectExtras: () => ({ optionFilterProp: "label" }),
    placeholderKey: "company_documents.recycling_processes.placeholder",
  } as KindConfig<RecyclingProcess, NoExt>,

  transaction: {
    resource: "transactions",
    select:
      "id, reference_number, external_id, load_date, unload_date, unloaded_weight, load_commercial_company_id, unload_commercial_company_id, load_place_company_id, unload_place_company_id, exporter_company_id, importer_company_id, load_commercial_company:companies!load_commercial_company_id(id,short_name), unload_commercial_company:companies!unload_commercial_company_id(id,short_name)",
    sorters:  [{ field: "created_at", order: "desc" }],
    buildFilters: () => [{ field: "is_deleted", operator: "eq", value: false }],
    labelFn: (tx: Transaction) =>
      `${tx.reference_number}${tx.external_id ? ` (${tx.external_id})` : ""}`,
    searchFn: (tx: Transaction) =>
      [tx.reference_number, tx.external_id].filter(Boolean).join(" "),
    optionData: (tx: Transaction) => ({ transaction: tx }),
    placeholderKey: "transactions.placeholders.select",
  } as KindConfig<Transaction, NoExt>,
};

// ─── Public Inputs ──────────────────────────────────────────────────────────

type BaseSelectProps = Omit<SelectProps, "options" | "loading" | "mode"> & {
  /** AntD `mode` (multiple/tags). */
  mode?: SelectProps["mode"];
};

export type EntitySelectProps<K extends EntityKind = EntityKind> =
  BaseSelectProps & {
    /** Which entity kind to show. Drives every per-domain decision. */
    kind: K;
    /** Cache duration for the underlying useList query (ms). Default 5 min. */
    staleTime?: number;
    /** Whether to fetch (default true). */
    enabled?: boolean;
    /** Optional testid forwarded to the AntD root via className-free wrapper. */
    testId?: string;
    /**
     * Pre-resolved option for the currently-selected record. Appended to the
     * options list (deduped by value) so the AntD Select always has a label
     * to render — even when the value is excluded by `buildFilterFn` (e.g.
     * an employee whose `roles` no longer matches the `role` filter) or
     * before the list query has returned.
     */
    extraOption?: { value: number | string; label: React.ReactNode } | null;
  } & KindExt[K];

// ─── Trigger min-width style singleton ──────────────────────────────────────
//
// The select trigger must never collapse below a readable minimum width.
// Injected once, guarded by element id (same pattern as SectionTabs).

const SELECT_MIN_WIDTH_STYLE_ID = "__ds-entity-select";

function ensureEntitySelectStyle(): void {
  if (typeof document === "undefined" || document.getElementById(SELECT_MIN_WIDTH_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = SELECT_MIN_WIDTH_STYLE_ID;
  el.textContent = `.ds-entity-select { min-width: ${LAYOUT.selectMinWidth}px; }`;
  document.head.appendChild(el);
}

// ─── Implementation ─────────────────────────────────────────────────────────

export function EntitySelect<K extends EntityKind>(props: EntitySelectProps<K>) {
  const { t } = useTranslation();
  const { kind, staleTime, enabled, testId, extraOption, ...rest } = props;

  // Pull every kind-specific ext prop out of `rest` so they don't leak into AntD.
  const ext = (rest as unknown) as KindExt[K];
  const stripped = { ...(rest as object) };
  for (const k of EXT_KEYS[kind]) {
    delete (stripped as Record<string, unknown>)[k];
  }
  const selectProps = stripped as BaseSelectProps;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cfg = REGISTRY[kind] as KindConfig<any, KindExt[K]>;
  const resource = typeof cfg.resource === "function" ? cfg.resource(ext) : cfg.resource;
  const select   = typeof cfg.select   === "function" ? cfg.select(ext)   : cfg.select;

  // Effective cache window: explicit prop wins, then per-kind default, then 5m.
  // `gcTime` matches so the data survives unmount/remount for the same window.
  const effectiveStaleTime = staleTime ?? cfg.staleTime ?? 5 * 60 * 1000;

  const [searchValue, setSearchValue] = useState<string>("");

  // Optional pre-fetch (e.g. wasteCode `onlyFromProducts`).
  const prefetchSpec = useMemo(
    () => cfg.allowedValuesPrefetch?.(ext) ?? null,
    [cfg, ext],
  );
  const {
    query: { data: prefetchData, isLoading: prefetchLoading },
  } = useList<Record<string, unknown>>({
    resource:    prefetchSpec?.resource ?? resource,
    meta:        prefetchSpec?.select ? { select: prefetchSpec.select } : undefined,
    pagination:  { mode: "off" },
    queryOptions: { enabled: !!prefetchSpec?.enabled, staleTime: effectiveStaleTime, gcTime: effectiveStaleTime },
  });
  const allowedKeys = useMemo(
    () =>
      !prefetchSpec || !prefetchData?.data
        ? null
        : new Set(prefetchData.data.map(prefetchSpec.extractKey).filter(Boolean)),
    [prefetchSpec, prefetchData],
  );

  // Main list query.
  const filters = cfg.buildFilters?.(ext);
  const {
    query: { data, isLoading },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useList<any>({
    resource,
    filters,
    sorters: cfg.sorters,
    pagination: { mode: "off" },
    meta:       select ? { select } : undefined,
    queryOptions: { staleTime: effectiveStaleTime, gcTime: effectiveStaleTime, enabled },
  });

  const filterFn = cfg.buildFilterFn?.(ext);

  const filteredRecords = useMemo(() => {
    // Rows are heterogeneous across kinds; `cfg` is the per-kind reader and
    // already carries the concrete row type in its own signatures.
    let items = (data?.data ?? []) as Array<Record<string, unknown>>;
    if (filterFn) items = items.filter((r) => filterFn(r));
    if (prefetchSpec && allowedKeys) {
      items = items.filter((r) => allowedKeys.has(prefetchSpec.matchKey(r)));
    }
    if (!searchValue.trim()) return items;
    const terms = searchValue.toLowerCase().split(/\s+/);
    return items.filter((r) => {
      const labelNode = cfg.labelFn(r, ext);
      const text = cfg.searchFn
        ? cfg.searchFn(r)
        : typeof labelNode === "string" || typeof labelNode === "number"
          ? String(labelNode)
          : "";
      const lower = text.toLowerCase();
      return terms.every((term) => lower.includes(term));
    });
  }, [data?.data, searchValue, filterFn, prefetchSpec, allowedKeys, cfg, ext]);

  const groupedOptions = useMemo(
    () => (cfg.groupBy ? cfg.groupBy(filteredRecords, t) : null),
    [filteredRecords, cfg, t],
  );

  const flatOptions = useMemo(() => {
    if (groupedOptions) return null;
    const base = filteredRecords.map((r) => {
      const value = cfg.valueFn ? cfg.valueFn(r) : (r as { id: number | string }).id;
      const opt: { label: React.ReactNode; value: number | string; [k: string]: unknown } = {
        // Wrap label in a span carrying a stable testid so e2e specs can
        // target options by value (escapes rc-virtual-list viewport check
        // + locale-dependent label match).
        label: (
          <span data-testid={`entity-select-option-${String(value)}`}>
            {cfg.labelFn(r, ext)}
          </span>
        ),
        value,
      };
      if (cfg.optionData) Object.assign(opt, cfg.optionData(r));
      return opt;
    });
    // Prepend the caller's pre-resolved option for the current value when it
    // isn't already represented (filtered out by `buildFilterFn`, or before
    // the list has loaded). Keeps the AntD Select from rendering raw IDs.
    if (extraOption && !base.some((o) => o.value === extraOption.value)) {
      return [extraOption, ...base];
    }
    return base;
  }, [filteredRecords, groupedOptions, cfg, ext, extraOption]);

  const options = (groupedOptions ?? flatOptions) as SelectProps["options"];

  const extras: Partial<SelectProps> = cfg.selectExtras?.(ext) ?? {};

  // Kind-supplied optionRender wins; otherwise every option label wraps
  // instead of truncating (dropdown rows must never clip text).
  const cfgOptionRender = cfg.optionRender;
  const optionRender = cfgOptionRender
    ? (option: unknown) => cfgOptionRender(option as { label: React.ReactNode; data?: Record<string, unknown> }, ext)
    : (option: unknown) => (
        <TextValue as="span" wrapWords>
          {(option as { label: React.ReactNode }).label}
        </TextValue>
      );

  ensureEntitySelectStyle();

  // Default fuzzy search via onSearch unless the kind opted into AntD's own filterOption.
  const usesAntdFilter = "filterOption" in extras;

  const placeholder =
    selectProps.placeholder ?? t(cfg.placeholderKey, cfg.placeholderFallback ?? "");

  const loading = isLoading || (prefetchSpec ? prefetchLoading : false);

  // `maxTagCount="responsive"` cannot work on this control. AntD sizes
  // `.ant-select` shrink-to-fit (`inline-flex`; callers pass only a `minWidth`
  // floor), while responsive mode asks rc-overflow to decide how many tags fit
  // by measuring each one — and it measures a non-displayed tag while that tag
  // is `position: absolute`, where `.ant-select-selection-item`'s
  // `max-width: 100%` resolves against the select's own width. Tag width then
  // depends on the select width, which depends on how many tags are displayed:
  // the loop has no fixed point, so the trigger oscillates between the full
  // label and the "+N" pill, and can settle collapsed at `minWidth` with a
  // whole free row beside it — measured hidden-tag widths track the container
  // exactly (container − 33px). It bites every kind, not just `company` (whose
  // ReactNode label makes it most visible); short string labels collapse to
  // zero visible tags at the 200px floor too.
  //
  // A fixed count skips rc-overflow's measurement entirely. Hosts that DO give
  // the control a definite width (a real `Form.Item` adds
  // `.ant-select-in-form-item { width: 100% }`) could support `responsive`, but
  // one tag + "+N" everywhere is the agreed behaviour — deterministic beats
  // "as many as fit" here (2026-08-18).
  const maxTagCount =
    selectProps.maxTagCount === "responsive" ? 1 : selectProps.maxTagCount;

  // Stamp `${testId}-search` on the rc-select-owned type-to-filter <input> via a
  // ref (display:contents wrapper → no layout box), so specs fill the search
  // deterministically instead of getByRole("combobox").
  const searchRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!testId || !searchRef.current) return;
    const input = searchRef.current.querySelector(".ant-select-selection-search-input");
    if (input && !input.getAttribute("data-testid")) {
      input.setAttribute("data-testid", `${testId}-search`);
    }
  });

  return (
    <span ref={searchRef} className="ds-display-contents">
    <Select
      showSearch
      loading={loading}
      options={options}
      filterOption={usesAntdFilter ? undefined : false}
      popupMatchSelectWidth={false}
      onSearch={usesAntdFilter ? undefined : setSearchValue}
      onBlur={() => setSearchValue("")}
      placeholder={placeholder}
      notFoundContent={
        loading ? t("common.messages.loading") : t("common.messages.noResults")
      }
      optionRender={optionRender}
      data-testid={testId}
      // Tag THIS select's dropdown popup with a testid derived from the select's
      // own testId so option lookups can be scoped deterministically (option
      // testids are keyed by row value — not unique across selects). No aria.
      popupRender={testId ? (menu) => <div data-testid={`${testId}-popup`} className="ds-display-contents">{menu}</div> : undefined}
      {...extras}
      {...selectProps}
      maxTagCount={maxTagCount}
      className={["ds-entity-select", (selectProps as { className?: string }).className]
        .filter(Boolean)
        .join(" ")}
    />
    </span>
  );
}

// ─── Per-kind extension prop key list (for prop stripping) ──────────────────

const EXT_KEYS: Record<EntityKind, readonly string[]> = {
  company:           ["category", "categories", "context", "requireNip", "excludeIds"],
  product:           ["context"],
  employee:          ["role"],
  payee:             [],
  wasteCode:         ["filterHazardous", "showHazardMark", "onlyFromProducts", "onlyEdprEligible"],
  recyclingProcess:  [],
  transaction:       [],
};
